import classes from '@/containers/pages/commonSettings/tabs/StoragePage/styles.module.css';
import { Button, Loader, Select } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IUsePopUpUI } from '@components/UI/PopUp';
import { storageConfigurationApiArea } from '@/services/api/storageConfigurationApiArea';
import { IStorageConfiguration } from '@/services/api/storageConfigurationApiArea/types';
import { showNotification } from '@mantine/notifications';

interface TransferDialogProps extends IUsePopUpUI {
  fromStorageId: number;
  onSuccess?: () => void;
  onMigrationStarted?: (migrationId: number, toStorageId: number) => void;
}

const TransferDialog = (props: TransferDialogProps) => {
  const { t } = useTranslation();
  const { handleClosePopUp, fromStorageId, onMigrationStarted } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [storages, setStorages] = useState<IStorageConfiguration[]>([]);
  const [selectedStorage, setSelectedStorage] = useState<string | null>(null);

  useEffect(() => {
    const fetchStorages = async () => {
      try {
        setIsFetching(true);
        const data = await storageConfigurationApiArea().GetStorageConfigurations();

        // Исключаем текущее хранилище и недоступные
        const availableStorages = data.filter(
          (storage) => storage.Id !== fromStorageId && storage.IsEnabled
        );

        setStorages(availableStorages);
      } catch (error) {
        // Игнорируем ошибку
      } finally {
        setIsFetching(false);
      }
    };

    fetchStorages();
  }, [fromStorageId]);

  const handleTransfer = async () => {
    if (!selectedStorage) return;

    try {
      setIsLoading(true);
      const migrationData = await storageConfigurationApiArea().MigrateStorage({
        from: fromStorageId,
        to: Number(selectedStorage)
      });

      showNotification({
        title: t('storagePage.notifications.migrationStarted'),
        message: '',
        autoClose: true,
        color: 'green',
        style: { zIndex: 1003 }
      });

      // Теперь ответ содержит IMigrationStatus с полем TaskId
      if (onMigrationStarted) {
        onMigrationStarted(migrationData.TaskId, Number(selectedStorage));
      }

      // Закрываем диалог выбора хранилища
      handleClosePopUp();
    } catch (error) {
      showNotification({
        title: t('storagePage.errors.migratingStorage'),
        message: '',
        autoClose: true,
        color: 'red',
        style: { zIndex: 1003 }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    handleClosePopUp();
  };

  const storageOptions = storages.map((storage) => ({
    value: storage.Id.toString(),
    label: storage.Name
  }));

  if (isFetching) {
    return (
      <div className={classes.transferDialog}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <Loader color="blue" />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.transferDialog}>
      <div className={classes.transferDialogQuestion}>{t('storagePage.transferDialog.title')}</div>
      <Select
        value={selectedStorage}
        rightSection={<IconChevronDown size="1rem" color="#98A2B3" />}
        onChange={setSelectedStorage}
        className={classes.select}
        data={storageOptions}
        label={t('storagePage.transferDialog.targetLabel')}
        placeholder={t('storagePage.transferDialog.targetPlaceholder')}
        comboboxProps={{
          withinPortal: false,
          transitionProps: { transition: 'pop', duration: 200 },
          offset: -20
        }}
        disabled={storages.length === 0}
      />
      {storages.length === 0 && (
        <div style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>
          {t('storagePage.transferDialog.noStoragesAvailable')}
        </div>
      )}
      <div className={classes.transferDialogActions}>
        <Button
          fullWidth={true}
          color="gray"
          variant="light"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {t('storagePage.transferDialog.cancelButton')}
        </Button>
        <Button
          fullWidth={true}
          color="blue"
          variant="light"
          onClick={handleTransfer}
          disabled={!selectedStorage || isLoading}
          loading={isLoading}
        >
          {t('storagePage.transferDialog.transferButton')}
        </Button>
      </div>
    </div>
  );
};

export default TransferDialog;
